import type { Menu } from "@/contexts/SidebarContext";
import { hasAnyPermission, hasAnyRole } from "@/utils/permissions";

export interface MenuAccessContext {
  user?: unknown;
  userPermissions?: string[];
}

const getApplicationCapabilities = (user: unknown): string[] => {
  if (!user || typeof user !== "object") return [];
  const candidate = user as { applicationCapabilities?: unknown; application_capabilities?: unknown };
  return [
    ...(Array.isArray(candidate.applicationCapabilities) ? candidate.applicationCapabilities : []),
    ...(Array.isArray(candidate.application_capabilities) ? candidate.application_capabilities : [])
  ].filter((capability): capability is string => typeof capability === "string");
};

export const hasMenuAccess = (menu: Menu, context: MenuAccessContext): boolean => {
  const userPermissions = context.userPermissions || [];
  const hasPermissionAccess = !menu.permissions || hasAnyPermission(userPermissions, menu.permissions as string[]);
  const hasRoleAccess = !menu.roles || hasAnyRole(context.user, menu.roles);
  const hasStaticAccess =
    menu.permissions && menu.roles
      ? hasPermissionAccess || hasRoleAccess
      : menu.permissions
        ? hasPermissionAccess
        : menu.roles
          ? hasRoleAccess
          : true;
  if (!hasStaticAccess) return false;

  const requiredCapabilities = menu.applicationCapabilities || [];
  return (
    requiredCapabilities.length === 0 ||
    requiredCapabilities.some((capability) => getApplicationCapabilities(context.user).includes(capability))
  );
};

export const hasVisibleMenuItem = (menu: Menu, context: MenuAccessContext): boolean => {
  if (menu.hidden) return false;
  if (!menu.subMenu?.length) return hasMenuAccess(menu, context);
  if (menu.applicationCapabilities?.length && !hasMenuAccess(menu, context)) return false;
  return menu.subMenu.some((child) => hasVisibleMenuItem(child, context));
};
